import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PortOne from "@portone/browser-sdk/v2";
import { ArrowLeft, CheckCircle, ChevronLeft, ChevronRight, ShoppingCart, Tag, XCircle } from "lucide-react";
import { PG_CHANNELS, PORTONE_STORE_ID, PRODUCTS } from "./catalog";

function randomPaymentId() {
  return [...crypto.getRandomValues(new Uint32Array(2))]
    .map((word) => word.toString(16).padStart(8, "0"))
    .join("");
}

export default function TestProductDetailPage() {
  const navigate = useNavigate();
  const { productKey } = useParams();
  const [paymentStatus, setPaymentStatus] = useState({ status: "IDLE" });
  const [isPaying, setIsPaying] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const pg = PG_CHANNELS.kakao;
  const product = productKey ? PRODUCTS[productKey] : undefined;
  const detailImages = product?.detailImages?.length ? product.detailImages : [product?.image].filter(Boolean);

  const closeDialog = () => setPaymentStatus({ status: "IDLE" });

  useEffect(() => {
    setCurrentImageIndex(0);
  }, [productKey]);

  const handlePrevImage = () => {
    if (!detailImages.length) {
      return;
    }

    setCurrentImageIndex((prev) => (prev === 0 ? detailImages.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    if (!detailImages.length) {
      return;
    }

    setCurrentImageIndex((prev) => (prev === detailImages.length - 1 ? 0 : prev + 1));
  };

  const handlePayment = async () => {
    if (!product) {
      return;
    }

    setIsPaying(true);
    setPaymentStatus({ status: "PENDING" });

    try {
      const paymentId = randomPaymentId();
      const response = await PortOne.requestPayment({
        storeId: PORTONE_STORE_ID,
        channelKey: pg.channelKey,
        paymentId,
        orderName: product.name,
        totalAmount: product.price,
        currency: product.currency,
        payMethod: pg.payMethod,
        ...(pg.easyPay ? { easyPay: pg.easyPay } : {}),
      });

      if (response.code !== undefined) {
        setPaymentStatus({ status: "FAILED", message: response.message });
      } else {
        setPaymentStatus({ status: "PAID", paymentId: response.paymentId });
      }
    } catch (e) {
      setPaymentStatus({
        status: "FAILED",
        message: e.message || "결제 중 오류가 발생했습니다.",
      });
    } finally {
      setIsPaying(false);
    }
  };

  if (!product) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-gray-900 border border-gray-800 rounded-3xl p-8 text-center">
          <p className="text-xl font-bold">상품을 찾을 수 없습니다.</p>
          <button
            onClick={() => navigate("/test")}
            className="mt-6 inline-flex items-center justify-center rounded-xl bg-white text-black px-4 py-2 font-semibold"
          >
            목록으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white px-4 py-10">
      <div className="fixed inset-0 opacity-20 pointer-events-none">
        <div className="absolute top-10 left-10 w-64 h-64 bg-yellow-400 rounded-full mix-blend-multiply filter blur-2xl animate-blob"></div>
        <div className="absolute bottom-10 right-10 w-64 h-64 bg-blue-400 rounded-full mix-blend-multiply filter blur-2xl animate-blob animation-delay-2000"></div>
      </div>

      <div className="relative z-10 w-full max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
            <Tag className="w-5 h-5" />
          </div>
          <span className="text-xl font-bold">TMTT.Link</span>
          <span className={`ml-2 text-xs border rounded-full px-2 py-0.5 ${pg.badgeClass}`}>
            {pg.label} 테스트
          </span>
        </div>

        <button
          onClick={() => navigate("/test")}
          className="inline-flex items-center gap-2 text-sm text-gray-300 hover:text-white transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          상품 목록으로
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-gray-900 border border-gray-800 rounded-3xl overflow-hidden">
            <div className="relative w-full bg-gray-800" style={{ aspectRatio: "891 / 1260" }}>
              <img
                src={detailImages[currentImageIndex]}
                alt={`${product.name} 상세 이미지 ${currentImageIndex + 1}`}
                className="w-full h-full object-cover"
              />

              {detailImages.length > 1 && (
                <>
                  <button
                    onClick={handlePrevImage}
                    className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/55 border border-white/10 text-white flex items-center justify-center hover:bg-black/75 transition-colors"
                    aria-label="이전 이미지"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={handleNextImage}
                    className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/55 border border-white/10 text-white flex items-center justify-center hover:bg-black/75 transition-colors"
                    aria-label="다음 이미지"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  <div className="absolute bottom-3 right-3 rounded-full bg-black/55 px-3 py-1 text-xs text-gray-200 border border-white/10">
                    {currentImageIndex + 1} / {detailImages.length}
                  </div>
                </>
              )}
            </div>

            {detailImages.length > 1 && (
              <div className="grid grid-cols-3 gap-3 p-4 bg-black/20 border-t border-gray-800">
                {detailImages.map((image, index) => (
                  <button
                    key={image}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`relative overflow-hidden rounded-2xl border transition-colors ${
                      currentImageIndex === index ? "border-yellow-300" : "border-gray-800 hover:border-gray-600"
                    }`}
                  >
                    <div className="w-full bg-gray-800" style={{ aspectRatio: "891 / 1260" }}>
                      <img
                        src={image}
                        alt={`${product.name} 썸네일 ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="bg-gray-900 border border-gray-800 rounded-3xl p-6 lg:p-8 flex flex-col">
            <p className="text-sm text-yellow-300 mb-3">{product.description}</p>
            <h1 className="text-3xl font-bold leading-tight">{product.name}</h1>
            <p className="text-gray-300 mt-4 leading-7">{product.headline}</p>
            <p className="text-gray-400 mt-4 leading-7">{product.detail}</p>

            <div className="mt-6 rounded-2xl border border-gray-800 bg-black/30 p-4">
              <p className="text-sm font-semibold mb-3">상품 특징</p>
              <div className="space-y-2 text-sm text-gray-300">
                {product.features.map((feature) => (
                  <div key={feature} className="flex items-start gap-2">
                    <span className="mt-1 h-2 w-2 rounded-full bg-yellow-300 shrink-0"></span>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 rounded-2xl border border-gray-800 bg-black/30 p-5">
              <div className="flex items-end justify-between gap-4">
                <div>
                  <p className="text-sm text-gray-500">판매가</p>
                  <p className="text-3xl font-bold mt-1">{product.price.toLocaleString()}원</p>
                </div>
                <span className={`text-xs border rounded-full px-2 py-1 ${pg.badgeClass}`}>
                  {pg.infoText}
                </span>
              </div>
              <button
                onClick={handlePayment}
                disabled={isPaying}
                className={`mt-5 w-full flex items-center justify-center gap-2 font-bold rounded-2xl px-5 py-4 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${pg.buttonClass}`}
              >
                {isPaying ? (
                  <span>처리 중...</span>
                ) : (
                  <>
                    <ShoppingCart className="w-4 h-4" />
                    카카오페이로 결제하기
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {paymentStatus.status === "FAILED" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-8 max-w-sm mx-4 text-center">
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <h3 className="text-xl font-bold text-red-400 mb-3">결제 실패</h3>
            <p className="text-gray-300 mb-6 text-sm">{paymentStatus.message}</p>
            <button
              onClick={closeDialog}
              className="bg-gray-700 hover:bg-gray-600 text-white rounded-full px-6 py-2 font-medium transition-colors"
            >
              닫기
            </button>
          </div>
        </div>
      )}

      {paymentStatus.status === "PAID" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-8 max-w-sm mx-4 text-center">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
            <h3 className="text-xl font-bold text-green-400 mb-3">결제 성공</h3>
            <p className="text-gray-300 mb-2 text-sm">결제가 완료되었습니다.</p>
            <p className="text-gray-500 text-xs mb-6 break-all">ID: {paymentStatus.paymentId}</p>
            <button
              onClick={closeDialog}
              className="bg-gray-700 hover:bg-gray-600 text-white rounded-full px-6 py-2 font-medium transition-colors"
            >
              닫기
            </button>
          </div>
        </div>
      )}

      <style jsx global>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob { animation: blob 7s infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
      `}</style>
    </div>
  );
}
